import { Component, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Columns, Config, DefaultConfig } from 'ngx-easy-table';
import { PaginationMeta } from 'src/app/model/pagination';
import { Product, ProductConfirm, ProductStatus } from 'src/app/model/product';
import { User } from 'src/app/model/user';
import { CheckDesktopService } from 'src/app/service/check-desktop.service';
import { MessageService } from 'src/app/service/message.service';
import { ProductService } from 'src/app/service/product.service';
import { AuthService } from '../../../service/auth.service';
import { PaymentService } from '../../../service/payment.service';
import { getHostProductEditUrl } from 'src/app/util/host-product-urls';
import { filterReviewConfirms, hasActiveApproval, isOpenGateRejected } from 'src/app/util/product-review';
import { failIfSwallowed } from 'src/app/util/swallowed-http';
import { LatestRequestGuard } from 'src/app/util/latest-request';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';

// statusBlockReason이 돌려주는 사유 문자열 — needsReviewRequest가 그중 하나를 식별할 수 있게 상수로 둔다.
const TITLE_REQUIRED_REASON = '상품 이름을 입력한 뒤 열 수 있습니다.';
const REJECTED_REASON = '반려된 상품입니다. 반려 사유를 반영해 수정한 뒤 검수를 다시 요청해주세요.';
const NOT_APPROVED_REASON = '검수 승인을 받은 상품만 열 수 있습니다. 「검수 요청」 후 승인을 받아주세요.';
const REVIEW_REQUEST_HINT = '눌러서 검수 요청하러 가기';

@Component({
  selector: 'hosted-meetings',
  templateUrl: './hosted-meetings.page.html',
  styleUrls: ['./hosted-meetings.page.scss'],
})
export class HostedMeetingsPage implements OnInit, OnDestroy {
  @ViewChild('checkTpl', { static: false }) checkTpl: TemplateRef<any>;

  // Constants for timing
  private readonly MODAL_OPEN_DELAY_MS = 500;
  private readonly POLLING_INTERVAL_MS = 500;
  private readonly MAX_POLLING_ATTEMPTS = 20; // 10 seconds max

  host: User;
  hostedProducts: Product[];
  modalRef: BsModalRef;
  selectedProduct: Product;
  mainDomainUrl = environment.webDomain;
  isDesktop = false;
  meta: PaginationMeta;
  productsError = false;
  // 페이지 연타 시 늦게 도착한 이전 응답이 최신 상태를 덮지 않도록.
  private productsRequest = new LatestRequestGuard();

  // Subscriptions management
  private subscriptions = new Subscription();
  private pollingTimeoutId: any;
  private modalSubscription: Subscription;

  columns: Columns[];
  config: Config;
  pagination = {
    offset: 1,
    limit: 10,
    count: -1,
  };

  constructor(
    private authService: AuthService,
    private productService: ProductService,
    private router: Router,
    private route: ActivatedRoute,
    private paymentService: PaymentService,
    private modalService: BsModalService,
    private messageService: MessageService,
    private cds: CheckDesktopService,
  ) {}

  ngOnInit() {
    const authSub = this.authService.getCurrentUser().subscribe((resp) => {
      this.host = resp;
      this.setProducts(1, 10);
    });
    this.subscriptions.add(authSub);
    this.subscriptions.add(this.cds.isDesktop.subscribe((v) => (this.isDesktop = v)));

    this.config = { ...DefaultConfig };
    this.config.horizontalScroll = true;
    this.config.orderEnabled = false;
    this.config.paginationMaxSize = 11;
    this.columns = [
      { key: 'index', title: '번호', width: '1%' },
      { key: 'check', title: '승인/반려 내역', width: '1%' },
      { key: 'isBusiness', title: '구분', width: '1%' },
      { key: 'representationPhoto', title: '이미지', width: '1%' },
      { key: 'status', title: '상태', width: '1%' },
      { key: 'title', title: '제목', width: '1%' },
      { key: 'edit', title: '관리', width: '1%' },
      { key: 'date', title: '생성일/수정일', width: '1%' },
    ];

    // Check for query params to show rejection modal
    const querySub = this.route.queryParams.subscribe((params) => {
      if (params.showRejection === 'true' && params.productId) {
        const productId = parseInt(params.productId, 10);
        this.showRejectionModalForProduct(productId);
      }
    });
    this.subscriptions.add(querySub);
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscriptions.unsubscribe();

    // Clean up modal subscription if exists
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }

    // Clear polling timeout if exists
    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
    }
  }

  getStatusBadge(status) {
    return this.productService.getStatusBadge(status);
  }

  /**
   * 검수 내역으로 보여줄 기록. 관리자가 목록 토글로 상태만 바꾼 기록은 검수가 아니므로 제외한다 —
   * 그대로 두면 호스트에게 요청한 적 없는 "승인"이 토글을 켤 때마다 쌓여 보인다.
   */
  reviewConfirms(product: Product): ProductConfirm[] {
    return filterReviewConfirms(product?.productConfirms);
  }

  showChecks(product: Product, template) {
    this.selectedProduct = product;
    this.modalRef = this.modalService.show(template, { class: 'modal-lg' });

    // Clean up previous modal subscription if exists
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }

    // Clean up URL when modal is closed
    this.modalSubscription = this.modalRef.onHidden.subscribe(() => {
      // Remove query params to prevent modal from reopening on refresh
      if (this.route.snapshot.queryParams.showRejection || this.route.snapshot.queryParams.productId) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            showRejection: null,
            productId: null,
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }

      // Clean up modal subscription
      if (this.modalSubscription) {
        this.modalSubscription.unsubscribe();
        this.modalSubscription = null;
      }
    });
  }

  /** 공백만 있는 제목은 서버 판정(blank)과 같이 비어 있는 것으로 본다. */
  displayTitle(product: Product): string {
    return product.title?.trim() || '(제목 없음)';
  }

  private isOpen(product: Product): boolean {
    return product.status === ProductStatus.ENTERED;
  }

  /** 제목 없는 상품, 반려된 상품, 검수 승인을 받지 못한 상품은 열 수 없다 — 서버 게이트(assertHostCanOpen)와 같은 기준. */
  canToggleStatus(product: Product): boolean {
    if (product.isDraft) return false;
    if (this.isOpen(product)) return true;
    if (product.status !== ProductStatus.DISABLED) return false;
    return !this.statusBlockReason(product);
  }

  /**
   * 막힌 배지를 탭했을 때의 동작. 「검수필요」는 검수 요청 단계로 직접 보낸다(NNB-1101) — 단,
   * 「수정」 버튼도 비즈니스 상품에만 보이므로(actionButtons) 벙개모임엔 새 진입로를 열지 않는다.
   */
  onStatusBadgeClick(product: Product) {
    if (this.canLinkToReviewRequest(product)) {
      this.navigateToReviewRequest(product);
      return;
    }
    this.showBlockReason(product);
  }

  /** 「검수필요」이면서 비즈니스 상품일 때만 검수 요청 단계로 링크한다 — 그 외엔 토스트만 유지. */
  private canLinkToReviewRequest(product: Product): boolean {
    return this.needsReviewRequest(product) && product.isBusiness;
  }

  // window.location.href 대입은 실제 브라우저(Karma)에서 테스트가 그대로 실행하면 테스트
  // 러너가 그 URL로 이동해버리는 하드 네비게이션이다 — 테스트는 spyOn으로 가로챈다.
  navigateToReviewRequest(product: Product) {
    window.location.href = getHostProductEditUrl(product.id, 'review');
  }

  /** statusBlockReason과 짝 — 링크로 이어지는 경우만 사유 대신 다음 행동을 읽어준다. */
  statusBadgeAriaLabel(product: Product): string {
    const hint = this.canLinkToReviewRequest(product) ? REVIEW_REQUEST_HINT : this.statusBlockReason(product);
    return `${this.displayStatusLabel(product)} — ${hint}`;
  }

  /** 토글이 막힌 비활성 배지에 이유를 보여준다. 토글 가능하거나 비활성이 아니면 null. */
  statusBlockReason(product: Product): string | null {
    if (product.isDraft || this.isOpen(product) || product.status !== ProductStatus.DISABLED) return null;
    if (!product.title?.trim()) return TITLE_REQUIRED_REASON;
    // PB는 서버 게이트(assertHostCanOpen)와 같은 이유로 승인 체크 대상이 아니다 — 검수 절차
    // 자체가 없어 product_confirm 기록이 쌓이지 않는다.
    if (product.isPb) return null;
    if (isOpenGateRejected(product.productConfirms)) return REJECTED_REASON;
    if (!hasActiveApproval(product.productConfirms)) return NOT_APPROVED_REASON;
    return null;
  }

  /** 작성은 끝났지만 검수 요청을 한 적이 없는 상품 — '검수중'(PENDING_REVIEW)과 짝을 이루는 라벨이 필요하다. */
  private needsReviewRequest(product: Product): boolean {
    return this.statusBlockReason(product) === NOT_APPROVED_REASON;
  }

  /** 목록 배지에 보여줄 라벨. 작성 완료·검수 전 상품만 실제 status('비활성') 대신 '검수필요'로 보여준다. */
  displayStatusLabel(product: Product): string {
    return this.needsReviewRequest(product) ? '검수필요' : product.status || '---';
  }

  /** 토글이 막힌 배지를 탭했을 때 이유를 토스트로 띄운다 — 모바일에선 title 속성 호버 툴팁이 뜨지 않는다. */
  showBlockReason(product: Product) {
    const reason = this.statusBlockReason(product);
    if (reason) this.messageService.toast(reason);
  }

  toggleStatusLabel(product: Product): string {
    return this.isOpen(product) ? '눌러서 비활성으로 전환' : '눌러서 오픈중으로 전환';
  }

  toggleStatus(product: Product) {
    const opening = !this.isOpen(product);
    const next = opening ? ProductStatus.ENTERED : ProductStatus.DISABLED;
    this.messageService.popup({
      title: '상품 상태 변경',
      description: opening
        ? '상품을 다시 오픈하시겠습니까? 예약 가능한 일정이 있어야 오픈할 수 있습니다.'
        : '상품을 비활성하면 고객에게 보이지 않고 새 예약을 받지 않습니다. 비활성하시겠습니까?',
      confirmLabel: opening ? '오픈' : '비활성',
      confirmBtnClass: opening ? undefined : 'red',
      confirmHandler: () => {
        this.productService
          .manageProduct(product.id, { status: next })
          .pipe(failIfSwallowed())
          .subscribe({
            next: () => this.setProducts(this.pagination.offset, this.pagination.limit),
            error: (err) =>
              this.messageService.popupError(err, {
                title: '상품 상태 변경',
                fallback: '상품 상태를 변경하지 못했습니다. 잠시 후 다시 시도해주세요.',
              }),
          });
      },
      cancelLabel: '취소',
    });
  }

  edit(id: number) {
    // 위저드는 Next.js(`webDomain`)에서 호스팅한다. RC가 OFF이면 Next.js 페이지가
    // 다시 host.nonunbub.com의 Angular legacy 편집 화면으로 redirect한다.
    window.location.href = getHostProductEditUrl(id);
  }

  removeProduct(id: number) {
    this.messageService.popup({
      title: '상품 삭제',
      description: '정말로 삭제하시겠습니까?',
      confirmLabel: '확인',
      confirmBtnClass: 'red',
      confirmHandler: () => {
        this.paymentService.getPaymentsCountByProduct(id).subscribe((resp) => {
          if (resp > 0) {
            this.productService.manageProduct(id, { status: ProductStatus.DISABLED }).subscribe((resp) => {
              this.messageService.popup({
                title: '상품 관리',
                description: '결제한 고객이 있어 상품을 삭제 하지않고 비활성하였습니다.',
                cancelLabel: '확인',
              });
              this.setProducts(this.pagination.offset, this.pagination.limit);
            });
          } else {
            this.productService.deleteProduct(id).subscribe((resp) => {
              this.messageService.popup({
                title: '상품 관리',
                description: '상품을 삭제하였습니다.',
                cancelLabel: '확인',
              });
              this.setProducts(this.pagination.offset, this.pagination.limit);
            });
          }
        });
      },
      cancelLabel: '취소',
    });
  }

  setProducts(page: number, limit: number) {
    this.productsError = false;
    const isLatest = this.productsRequest.begin();
    this.productService
      .search({ page, limit, status: ProductStatus.ALL, hostId: this.host.id })
      .pipe(failIfSwallowed())
      .subscribe({
        next: (products) => {
          if (!isLatest()) return;
          this.hostedProducts = products.items.map((product) => {
            product.productConfirms = product.productConfirms.sort((a, b) => b.id - a.id);
            return product;
          });
          this.pagination = {
            limit: products.meta.itemsPerPage,
            offset: products.meta.currentPage,
            count: products.meta.totalItems,
          };
          this.meta = products.meta;
          this.meta.paginationId = 'host-product-table';
        },
        error: () => {
          if (!isLatest()) return;
          this.productsError = true;
          // 데스크톱은 표가 빈 채로 남아 "상품 없음"과 구분되지 않으므로 toast로 알린다(모바일 empty와 병행).
          this.messageService.toast('상품 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
        },
      });
  }

  onTableEvent(event: any): void {
    if (event.event === 'onPagination') {
      this.setProducts(event.value.page, event.value.limit);
    }
  }

  pageChanged(page: number) {
    this.setProducts(page, this.pagination.limit);
  }

  goToTicket(id: number) {
    this.router.navigate(['/host/schedule'], {
      queryParams: { productId: id },
    });
  }

  showRejectionModalForProduct(productId: number) {
    let attemptCount = 0;

    // Clear any existing polling timeout
    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
    }

    // Wait for products to be loaded
    const checkAndShow = () => {
      attemptCount++;

      if (this.hostedProducts && this.hostedProducts.length > 0) {
        const product = this.hostedProducts.find((p) => p.id === productId);
        // 표시할 검수 내역이 있을 때만 연다. 원본 배열로 판정하면 관리자 토글 기록만 있는 상품에서
        // 빈 표가 뜬다 — 표는 검수 기록만 그리기 때문이다.
        if (product && this.reviewConfirms(product).length > 0) {
          setTimeout(() => {
            if (this.checkTpl) {
              this.showChecks(product, this.checkTpl);
            }
          }, this.MODAL_OPEN_DELAY_MS);
        }
      } else if (attemptCount < this.MAX_POLLING_ATTEMPTS) {
        // If products not loaded yet, wait and retry
        this.pollingTimeoutId = setTimeout(checkAndShow, this.POLLING_INTERVAL_MS);
      } else {
        // Max attempts reached, stop polling
        console.warn(`Product with ID ${productId} not found after ${this.MAX_POLLING_ATTEMPTS} attempts`);
      }
    };

    checkAndShow();
  }
}
